import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChevronDown, LogOut, Compass } from "lucide-react";
import { TOUR_EVENT } from "@/components/WelcomeTour";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_LABEL = { admin: "Admin", coach: "Secretário", consultant: "Consultor", staff: "Staff" };

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "KW";

/** Account menu for the top-right corner: personal pages + session + logout. */
export const UserMenu = ({ items = [], compact = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "consultant";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 pl-1 pr-2 py-1 border border-[#222] hover:border-[#444] transition-colors group"
          data-testid="user-menu-trigger">
          <span className="w-7 h-7 bg-[#E50000] flex items-center justify-center font-display font-black text-[11px] leading-none shrink-0">
            {initials(user?.name)}
          </span>
          {!compact && (
            <span className="text-left leading-tight max-w-[140px]">
              <span className="block text-[11px] font-bold truncate">{user?.name}</span>
              <span className="block text-[9px] uppercase tracking-widest text-[#737373]">
                {ROLE_LABEL[role] || role}
              </span>
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-[#737373] group-hover:text-white transition-colors" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-[#0F0F0F] border-[#222] text-white">
        <DropdownMenuLabel className="pb-2">
          <div className="text-sm font-bold truncate" data-testid="user-menu-name">{user?.name}</div>
          <div className="text-[11px] text-[#737373] truncate font-normal">{user?.email}</div>
          <div className="mt-2 inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 bg-[#E50000]/10 text-[#E50000] border border-[#E50000]/30">
            {ROLE_LABEL[role] || role}{user?.club ? ` · ${user.club}` : ""}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#222]" />
        {items.map((n) => (
          <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)} data-testid={n.testid}
            className="gap-2 text-[#A3A3A3] focus:text-white focus:bg-[#1A1A1A] cursor-pointer uppercase tracking-wider text-[11px] font-bold font-display">
            <n.icon className="w-3.5 h-3.5" /> {n.label}
          </DropdownMenuItem>
        ))}
        {items.length > 0 && <DropdownMenuSeparator className="bg-[#222]" />}
        <DropdownMenuItem onClick={() => window.dispatchEvent(new Event(TOUR_EVENT))} data-testid="user-menu-tour"
          className="gap-2 text-[#A3A3A3] focus:text-white focus:bg-[#1A1A1A] cursor-pointer uppercase tracking-wider text-[11px] font-bold font-display">
          <Compass className="w-3.5 h-3.5" /> Guia de boas-vindas
        </DropdownMenuItem>
        <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn"
          className="gap-2 text-[#A3A3A3] focus:text-white focus:bg-[#1A1A1A] cursor-pointer uppercase tracking-wider text-[11px] font-bold font-display">
          <LogOut className="w-3.5 h-3.5" /> Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
